/*
 * The app router. Areas are discovered: every `src/features/<area>/routes.tsx` that exports
 * `routes: AreaRoutes` is placed under its layouts here, so areas never edit this file.
 *
 *   /                         RootRedirect (signup / login / welcome / overview)
 *   guest                     GuestOnly: sign up, sign in, 2FA, forgot password
 *   public                    no guard, no layout: email link targets, /dev/kit
 *   shell                     RequireAuth + AppShell (sidebar, top bar, banners)
 *   focus                     RequireAuth + FocusLayout (onboarding, wizards, /activate)
 *   *                         404
 */
import { Outlet, ScrollRestoration, createBrowserRouter, type RouteObject } from 'react-router'
import { AppShell } from '@/components/shell/AppShell'
import { FocusLayout } from '@/components/shell/FocusLayout'
import { SplashScreen } from '@/components/shell/SplashScreen'
import { SessionExpiredDialog } from '@/features/auth/components/SessionExpiredDialog'
import { NotFoundPage } from '@/features/system/NotFoundPage'
import { RouteError } from '@/features/system/RouteError'
import { GuestOnly, RequireAuth, RootRedirect } from './guards'
import type { AreaRoutes } from './routes'

const modules = import.meta.glob<{ routes?: AreaRoutes }>('/src/features/*/routes.tsx', { eager: true })
const areas = Object.values(modules)
  .map((m) => m.routes)
  .filter((r): r is AreaRoutes => Boolean(r))
const collect = (key: keyof AreaRoutes): RouteObject[] => areas.flatMap((a) => a[key] ?? [])

function RootLayout() {
  return (
    <>
      <Outlet />
      <SessionExpiredDialog />
      <ScrollRestoration />
    </>
  )
}

export const router = createBrowserRouter([
  {
    Component: RootLayout,
    HydrateFallback: SplashScreen,
    errorElement: <RouteError />,
    children: [
      {
        errorElement: <RouteError />,
        children: [
          { index: true, Component: RootRedirect },
          { Component: GuestOnly, children: collect('guest') },
          ...collect('public'),
          {
            Component: RequireAuth,
            children: [
              { Component: AppShell, children: collect('shell') },
              { Component: FocusLayout, children: collect('focus') },
            ],
          },
          { path: '*', Component: NotFoundPage },
        ],
      },
    ],
  },
])
